import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from './PracticeCard.module.scss';
import classes from 'classnames';
import TinderCard from 'react-tinder-card';
import { Question } from '@/types/question';
import SVG from 'react-inlinesvg';
import commonUtils from '@/utils/common';
import UseScreenSize from '@/hooks/UseScreenSize';
import { useAppSelector } from '@/store';
import { interfaceLanguageSelector, nativeLanguageSelector } from '@/store/reducers/auth';
import { gameSelector } from '@/store/reducers/game';
import { authSelector } from '@/store/reducers/auth';

import soundPlay from '@/assets/svg/speaker.svg';
import UseUnitsAssets from '@/hooks/UseUnitsAssets';
import { unitSelector } from '@/store/reducers/unit';
import { UnitAssets } from '@/types/units';
import { useTranslation } from 'react-i18next';

interface PracticeCardProps {
  question: Question;
  index: number;
  emitOnSwipe: (questionId: number) => void;
}

const PracticeCard = ({ question, index, emitOnSwipe }: PracticeCardProps) => {
  const { t } = useTranslation();
  const { isDesktop, isMobile } = UseScreenSize();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const nativeLanguage = useAppSelector(nativeLanguageSelector);
  const { selectedUnit } = useAppSelector(unitSelector);

  const { unitsAssets } = UseUnitsAssets();
  const unitAssets: UnitAssets = unitsAssets[selectedUnit?.order || 1];

  const [enableEvents, setEnableEvents] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showSoundBtn, setShowSoundBtn] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const [isRemoving, setIsRemoving] = useState(false);

  const { fromToLang } = useAppSelector(gameSelector);
  const { user } = useAppSelector(authSelector);

  const answerText = question?.newData?.data?.questionDataLocal?.answer?.text ?? question?.answer;

  const isOnNativeLanguage = useMemo(() => {
    return user?.metadata.languagePreferences.fromToLanguageParams?.[0] === fromToLang;
  }, [user, fromToLang]);

  useEffect(() => {
    setEnableEvents(() => !index);
  }, [index]);

  useEffect(() => {
    let delayDebounceFn = 0;

    if (isRemoving) {
      delayDebounceFn = setTimeout(() => {
        emitOnSwipe(question.questionId);
      }, 50) as any;
    }

    return () => clearTimeout(delayDebounceFn);
  }, [isRemoving]);

  const onFlip = useCallback(async () => {
    const practiceCard = document.querySelector(
      `.practiceCard-${question.questionId}`
    ) as HTMLDivElement;

    const matches = practiceCard?.style.transform.match(/translate3d\((.*?)\)/);

    if (!enableEvents) {
      return;
    }

    if (matches) {
      const points = matches[1];
      const pointsValues = points.split(', ').map(point => Math.abs(+point.replace('px', '')));

      const isMoved = pointsValues.some(point => point > 10);

      if (isMoved) {
        return;
      }
    }

    setIsFlipped(prevState => !prevState);
    setShowSoundBtn(false);
    await commonUtils.sleep(400);
    setShowSoundBtn(true);
  }, [question]);

  const onSwipe = useCallback(() => {
    if (!enableEvents) {
      return;
    }
    setIsRemoving(true);
  }, []);

  const onPlaySound = () => {
    if (question?.dictionaryDetails?.audio && !isPlaying) {
      const audio = new Audio(question.dictionaryDetails.audio);
      setIsPlaying(true);
      audio.addEventListener('ended', () => setIsPlaying(false));
      audio.addEventListener('error', () => setIsPlaying(false));
      audio.play();
    }
  };

  const content = enableEvents ? (
    <>
      {showSoundBtn && (!isMobile || !isFlipped) && question?.dictionaryDetails?.audio && (
        <button
          className={classNames.soundBtn}
          onClick={onPlaySound}
          disabled={isPlaying}
          aria-label={t('play_sound') || ''}
        >
          <SVG src={soundPlay} />
        </button>
      )}
      <div
        className={classNames.front}
        onTouchEndCapture={() => (isMobile ? onFlip() : null)}
        onClick={() => (isDesktop ? onFlip() : null)}
      >
        {question?.question && (
          <span
            className={classes({
              ['text-3xl']:
                ((isOnNativeLanguage ? question?.question : answerText) || '').length > 6,
            })}
            style={{
              direction:
                nativeLanguage?.direction === 'rtl' &&
                nativeLanguage?.interfaceLanguage === 'Hebrew' &&
                !isOnNativeLanguage
                  ? 'rtl'
                  : 'ltr',
            }}
          >
            {isOnNativeLanguage ? question?.question : answerText}
          </span>
        )}
        <span className={classNames.flipButton}></span>
      </div>
      <div
        className={classNames.back}
        onTouchEndCapture={() => (isMobile ? onFlip() : null)}
        onClick={() => (isDesktop ? onFlip() : null)}
      >
        <div className={classNames.top}>
          <span>{question?.question}</span>
          {question.dictionaryDetails?.partOfSpeech && (
            <div className={classNames.partOfSpeech}>
              <span>{question.dictionaryDetails?.partOfSpeech}</span>
            </div>
          )}
        </div>
        <div className={classNames.body}>
          <span
            className={classes(classNames.translation, {
              [classNames.translationRtl]: nativeLanguage?.direction === 'rtl',
            })}
          >
            {answerText}
          </span>
          <div className={classNames.seperator}></div>
          <div className={classNames.exampleSentences}>
            <div key={index} className={classNames.exampleSentence}>
              {question.dictionaryDetails?.exampleSentences}
            </div>
          </div>
        </div>
      </div>
    </>
  ) : null;

  const cardElement = (
    <div
      className={classes(classNames.card, [classNames[`card-${interfaceLanguage?.direction}`]], {
        [classNames.cardFlip]: isFlipped,
        [classNames[`card-${interfaceLanguage?.direction}`]]: isFlipped,
      })}
      style={{ backgroundColor: unitAssets?.modalBorderColor }}
    >
      {content}
    </div>
  );

  if (isDesktop) {
    return (
      <div
        className={classes(`practiceCard-${question.questionId}`, classNames.practiceCard, {
          'pointer-events-none': !enableEvents,
        })}
      >
        {cardElement}
      </div>
    );
  }

  return (
    <TinderCard
      className={classes(`practiceCard-${question.questionId}`, classNames.practiceCard, {
        'pointer-events-none': !enableEvents,
      })}
      onCardLeftScreen={() => onSwipe()}
    >
      {cardElement}
    </TinderCard>
  );
};

export default React.memo(PracticeCard);
